import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock3 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";

const FALLBACK_COVER = "https://images.unsplash.com/photo-1758518727613-00192aed759b?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api
      .get(`/blogs/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-32 text-center" data-testid="blog-post-missing">
        <h1 className="font-serif text-3xl text-slate-50">Article not found</h1>
        <Link to="/blog" className="btn-ghost mt-8" data-testid="blog-post-back-btn">Back to The HR Insight</Link>
      </div>
    );
  }

  if (!post) {
    return <div className="mx-auto max-w-3xl px-5 py-32 text-sm text-slate-500" data-testid="blog-post-loading">Loading article…</div>;
  }

  return (
    <article className="mx-auto max-w-3xl px-5 pb-24 pt-16 lg:pt-24" data-testid="blog-post-page">
      <Reveal>
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-[#C5A059]" data-testid="blog-post-back-link">
          <ArrowLeft size={15} /> The HR Insight
        </Link>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <span className="rounded-full border border-[#C5A059]/40 px-4 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#C5A059]">{post.category}</span>
          <span className="inline-flex items-center gap-1.5 font-mono text-xs text-slate-500"><Clock3 size={13} /> {post.read_time} min read</span>
        </div>
        <h1 className="mt-6 font-serif text-4xl font-semibold leading-tight text-slate-50 sm:text-5xl" data-testid="blog-post-title">{post.title}</h1>
        <p className="mt-5 font-serif text-xl italic leading-relaxed text-slate-400">{post.excerpt}</p>
        <div className="mt-8 flex items-center gap-4 border-y border-slate-800 py-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#C5A059] font-serif text-lg font-semibold text-[#0A0F1D]">R</div>
          <div>
            <div className="text-sm font-semibold text-slate-100">{post.author}</div>
            <div className="font-mono text-xs text-slate-500">{new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <img src={post.cover_image || FALLBACK_COVER} alt={post.title} className="mt-10 aspect-[16/8] w-full rounded-2xl border border-slate-800 object-cover" data-testid="blog-post-cover" />
      </Reveal>

      <Reveal delay={0.15}>
        <div className="prose-rms mt-12 space-y-6" data-testid="blog-post-content">
          {post.content.split("\n").filter(Boolean).map((para, i) => (
            <p key={i} className={`leading-relaxed ${para.match(/^\d\./) || para.endsWith(":") && para.length < 80 ? "text-slate-200 font-medium" : "text-slate-300"} text-base`}>
              {para}
            </p>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="card-surface gold-stroke mt-16 p-8 text-center lg:p-10" data-testid="blog-post-cta">
          <h3 className="font-serif text-2xl font-semibold text-slate-50">Ready for a CV that passes British screening?</h3>
          <p className="mx-auto mt-3 max-w-md text-sm text-slate-400">Get a recruiter-written, ATS-verified UK CV from £77 — crafted by the same HR lens behind this article.</p>
          <Link to="/order" className="btn-gold mt-7" data-testid="blog-post-cta-btn">
            Start Your Order <ArrowRight size={16} />
          </Link>
        </div>
      </Reveal>
    </article>
  );
}
